/**
 * ⚠️ CAMELCASE/SNAKE_CASE BOUNDARY GOTCHA — READ BEFORE TOUCHING SHAPES ⚠️
 *
 * The interceptors below run ``humps.camelizeKeys`` on EVERY JSON
 * response and ``humps.decamelizeKeys`` on EVERY JSON request body.
 * These are **deep, recursive** conversions — they rewrite keys at
 * every nesting level inside the JSON tree, including inside arrays
 * and inside JSON stored in Django ``JSONField`` columns.
 *
 * This has bitten us multiple times. The pattern of failure:
 *
 *  1. BE persists a structured payload to a ``JSONField`` column (a
 *     Pydantic ``model_dump(mode="json")`` result, an SSE event
 *     buffer, an analytics blob, …). The shape is snake_case because
 *     that's what the Python author types.
 *
 *  2. A different endpoint reads that JSONField back and returns it
 *     in a DRF serializer. The interceptor here recursively
 *     camelCases EVERYTHING, including the keys *inside* the stored
 *     blob — ``card_kind`` becomes ``cardKind``, ``label_template``
 *     becomes ``labelTemplate``, etc.
 *
 *  3. Meanwhile a non-axios path (SSE consumer, websocket, direct
 *     ``fetch``) reads the same payload shape and gets the ORIGINAL
 *     snake_case because it bypasses these interceptors entirely.
 *
 *  4. FE code written against one path silently breaks on the other.
 *     The most painful version of this is when a discriminator field
 *     (``card_kind``) drives a switch statement: the switch's
 *     exhaustive default sends the WHOLE payload object back as a
 *     React child, which then surfaces as
 *     ``"Objects are not valid as a React child"`` — far from the
 *     real cause.
 *
 * **Surfaces hit so far:**
 *   - ``ChatMessage.orchestrator_cards`` / ``.orchestrator_trace``
 *     (Phase 1 smoke — replay path camelCased the deep card
 *     payloads; ToolCardDispatcher's ``card_kind`` switch silently
 *     fell through. Fix landed in commit referenced by `ToolCardDispatcher`
 *     payload-normalizer and `tool-trace/replay.ts`.)
 *   - ``audit/serializers/NycCblEntry`` — also bitten when the SODA
 *     adapter persisted snake_case rows that round-tripped through
 *     ``humps`` on retrieval (rotated to a passthrough serializer).
 *   - ``ChatMessage.tool_results`` / ``.citations`` — same dual-shape
 *     class as the cards/trace; migrated to shared Zod codecs
 *     (``tool-results-schemas.ts`` / ``citations-schema.ts``) on the
 *     wire-codec branch.
 *   - ``audit.*Submission.filing_context`` / ``filing_context_snapshot``
 *     — ``surface_reason.meta`` (an opaque BE-owned record) was
 *     camelized in flight; now parsed at the ``getFilingContext``
 *     chokepoint via ``api/filingContext.ts::filingContextSchema``.
 *
 * **Rules going forward when you add a new feature:**
 *
 *  ✅ DO write a small **normalizer** at the FE consumer boundary
 *     that reads BOTH key spellings. The pattern:
 *
 *         const read = (...keys: string[]) => {
 *           for (const k of keys) if (k in raw) return raw[k];
 *           return undefined;
 *         };
 *         const labelTemplate = read("label_template", "labelTemplate");
 *
 *     This survives a deploy where SSE and axios disagree on which
 *     shape is in flight at a given moment. ``tool-trace/replay.ts``
 *     is the canonical example — copy that idiom.
 *
 *  ✅ DO render a TypeScript discriminator on the FE side using the
 *     KEY SPELLING THE FE EXPECTS, normalized at the boundary.
 *     Don't switch on the BE field name without normalizing first.
 *
 *  ❌ DON'T assume ``response.data`` from this axios instance is
 *     snake_case anywhere. It isn't. Period. Don't ``response.data.card_kind``.
 *
 *  ❌ DON'T rely on the camelizer for JSON stored in ``JSONField``.
 *     The camelizer is correct for top-level DRF serializer fields
 *     but wrong-by-default for opaque BE-owned payloads inside them.
 *
 *  ❌ DON'T fix this by **disabling** the interceptor for one
 *     endpoint. The codebase has thousands of consumers depending
 *     on the camelizer behavior; a per-endpoint exception would
 *     scatter the snake↔camel boundary unpredictably. Normalize at
 *     the consumer boundary, not the transport.
 *
 *  ⚠️ If you're authoring a NEW JSONField column whose payload will
 *     be read both via axios and via SSE/WebSocket, add a normalizer
 *     test that asserts both shapes parse to the same FE structure.
 *     The Phase 1 ``tool-trace/__tests__`` directory shows the
 *     pattern.
 *
 * ----------------------------------------------------------------------
 * DECISION (2026-06-09) — option 2 chosen: per-endpoint Zod codecs
 * ----------------------------------------------------------------------
 *
 * We evaluated five options (opt-out wire envelope, per-call codecs,
 * camelization allow-list, BE-side camelization, lint rule) and chose
 * **per-endpoint Zod codecs**, migrated by hazard, not alphabetically.
 * The mechanism lives in ``api/codec.ts``:
 *
 *   - ``{ rawResponse: true }`` on a request tells the response
 *     interceptor below to SKIP camelization — the caller receives
 *     verbatim snake_case, the SAME shape the SSE path sees.
 *   - ``codecGet`` / ``codecPost`` set that flag and run a Zod schema
 *     (built from ``dualKey`` — snake-first, camel-fallback) over the
 *     raw payload. The schema is transport-agnostic: feed it the
 *     axios-raw shape OR the SSE shape and it produces the same
 *     canonical output.
 *
 * **Codecs are the default for NEW endpoints.** The interceptor below
 * keeps serving every un-migrated pure-DRF endpoint unchanged and
 * shrinks over time. Migrated so far: the four ``ChatMessage``
 * JSONFields across both transports (replay ``codecGet`` + SSE through
 * the same schemas) and the ``filing_context`` payload via the
 * ``getFilingContext`` chokepoint.
 *
 * Option 5 (lint rule) also landed as a guardrail: ``.eslintrc.cjs``
 * forbids ``response.data.<snake_case_key>`` reads outside the codec
 * boundary files.
 *
 * Whoever touches this next: please update the **Surfaces hit so far**
 * list above with any NEW incidents you find while triaging the
 * codebase.
 *
 * — CTO note, 2026-05-12; updated 2026-06-09 (option 2 in progress)
 */
import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { camelizeKeys, decamelizeKeys } from "humps";
import qs from "qs";

const BASE_URL =
  // eslint-disable-next-line no-restricted-globals
  location.hostname === "localhost" ? "http://localhost:8000/api/" : "/api/";

const api = axios.create({
  baseURL: BASE_URL,
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
});

// Axios middleware to convert all api responses to camelCase.
//
// ``rawResponse`` opt-out (2026-06-09): a request that sets
// ``{ rawResponse: true }`` skips camelization entirely, so the caller
// receives verbatim snake_case — the SAME shape the SSE path sees. This
// is the systematic seam for the per-endpoint Zod-codec migration (see
// ``api/codec.ts`` and the option-2 plan in the CTO note above). It is
// NOT the per-endpoint interceptor-disable the note warns against: the
// codec takes typed ownership of the wire shape, so the snake↔camel
// boundary is centralized in the schema, not scattered.
api.interceptors.response.use((response: AxiosResponse) => {
  const rawResponse = response.config?.rawResponse === true;
  if (
    !rawResponse &&
    response.data &&
    response.headers["content-type"] === "application/json"
  ) {
    response.data = camelizeKeys(response.data);
  }

  return response;
});

/** Staff's own token while impersonating (components/common/ImpersonationMenu). */
export const ORIGINAL_TOKEN_KEY = "originalToken";
/** POST impersonate/ `impersonation` (account/impersonation.py mint): signed, 12 h, this customer only. */
export const IMPERSONATION_PROOF_KEY = "impersonationProof";

export const isImpersonating = () =>
  typeof window !== "undefined" && !!localStorage.getItem(ORIGINAL_TOKEN_KEY);

/** account/impersonation.py: only a verified proof makes the server name the staff member (Leo 10-21, log 191). */
export const IMPERSONATION_HEADER = "X-CodeClear-Impersonating";
/** Sent when no proof is stored: LL33 writes get 403, never the customer's stamp. */
export const UNVERIFIED_IMPERSONATION = "unverified";

/** The one builder of IMPERSONATION_HEADER (this interceptor, useLl33 ll33Headers); undefined unless impersonating. */
export const impersonationHeaders = () =>
  isImpersonating()
    ? {
        [IMPERSONATION_HEADER]:
          localStorage.getItem(IMPERSONATION_PROOF_KEY) ||
          UNVERIFIED_IMPERSONATION,
      }
    : undefined;

// Axios middleware to convert all api requests to snake_case
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const newConfig = { ...config };
  const impersonation = impersonationHeaders();
  if (impersonation) {
    newConfig.headers.set(impersonation);
  }
  const contentType = newConfig?.headers?.["Content-Type"];
  if (newConfig.data instanceof FormData) {
    return newConfig;
  }
  if (contentType === "multipart/form-data") {
    return newConfig;
  }

  if (config.params) {
    newConfig.params = decamelizeKeys(config.params);
  }

  if (config.data) {
    newConfig.data = decamelizeKeys(config.data);
  }

  return newConfig;
});

export const setupAxios = (token?: string) => {
  if (token) {
    api.defaults.headers.common.Authorization = `Token ${token}`;
  } else {
    api.defaults.headers.common.Authorization = null;
  }
};

const token = localStorage.getItem("token");
if (token) {
  setupAxios(token);
}

/** Authorization is set once, above, while the impersonation header reads storage per request: after another
 * tab signs in, out or into a customer, a staff click here would land as the customer. Reload instead. */
const IDENTITY_KEYS = ["token", ORIGINAL_TOKEN_KEY, IMPERSONATION_PROOF_KEY];
window.addEventListener("storage", (event) => {
  if (event.key && IDENTITY_KEYS.includes(event.key)) {
    window.location.reload();
  }
});

export default api;
